import { t } from "@travily/i18n";
import { formatPriceWhole } from "@ui/lib/format-price";
import { tv } from "@ui/lib/tv";
import { type Costs, PUBLISHED_CURRENCY } from "../../apply-household";
import { ProvenanceBadge } from "./ProvenanceBadge";
import { ProvenancePopover } from "./ProvenancePopover";

const costHeadline = tv({
  slots: {
    root: "flex flex-col gap-3",
    amountRow: "flex flex-wrap items-baseline gap-2",
    amount: "font-extrabold text-[34px] text-neutral-900 tabular-nums leading-10 tracking-[-0.34px]",
    per: "text-lg text-neutral-600",
    // The band, as a plain track: the figure is a distribution read at one
    // point, and a bare number would claim a precision the panel never has.
    track: "relative h-2 w-full max-w-sm overflow-hidden rounded-full bg-neutral-100",
    span: "absolute inset-y-0 rounded-full bg-red-100",
    marker: "absolute inset-y-0 w-1 rounded-full bg-red-400",
    band: "text-neutral-600 text-sm tabular-nums",
    meta: "flex flex-wrap items-center gap-3",
  },
  variants: {
    pending: { true: { amount: "opacity-60" } },
  },
});

export interface CostHeadlineProps {
  total: Costs["total"];
  attributions: Costs["attributions"];
  methodVersion: number;
  /** True while the server is re-quoting a household the reader just changed. */
  pending?: boolean;
}

/** Where the marker sits inside the band, as a percentage of it. */
function markerOffset(low: number, mid: number, high: number): number {
  if (high <= low) return 50;
  return Math.min(100, Math.max(0, ((mid - low) / (high - low)) * 100));
}

export function CostHeadline({ attributions, methodVersion, pending = false, total }: CostHeadlineProps) {
  const styles = costHeadline({ pending });
  const { p25_usd_minor: low, p50_usd_minor: mid, p75_usd_minor: high } = total;
  const hasBand = low !== null && high !== null && mid !== null;

  return (
    <div className={styles.root()}>
      <div className={styles.amountRow()}>
        <span className={styles.amount()}>{mid === null ? "—" : formatPriceWhole(mid, PUBLISHED_CURRENCY)}</span>
        <span className={styles.per()}>{t("components.views.destinations.costs.per_month")}</span>
      </div>

      {hasBand && (
        <>
          <div className={styles.track()}>
            <span className={styles.span()} style={{ left: "0%", right: "0%" }} />
            <span className={styles.marker()} style={{ left: `${markerOffset(low, mid, high)}%` }} />
          </div>
          <p className={styles.band()}>
            {t("components.views.destinations.costs.band", {
              low: formatPriceWhole(low, PUBLISHED_CURRENCY),
              high: formatPriceWhole(high, PUBLISHED_CURRENCY),
            })}
          </p>
        </>
      )}

      <div className={styles.meta()}>
        <ProvenanceBadge grade={total.grade} />
        <ProvenancePopover
          attributions={attributions}
          computed={!total.as_of_observed}
          methodVersion={methodVersion}
          total={total}
        />
      </div>
    </div>
  );
}
